import {
  Button,
  Col,
  Modal,
  Row,
  Select,
  message,
  Spin,
  Popover,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import { DotsCategories } from "@dots-projects/types";
import { Controller, useForm } from "react-hook-form";
import { FormLabel } from "../TriggerEditor/FormLabel";
import "./Admin.css";
import { Input } from "../TriggerEditor/Input";
import useDotsAdmin from "./useDotsAdmin";
import { useQueryClient } from "react-query";
import Icon, { InfoCircleOutlined } from "@ant-design/icons";
import AdminCategories from "./AdminCategories";
import { createDiscordUrl } from "../../utils/constants";
import { useSearchParams } from "react-router-dom";

const successStatusSvg = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ margin: "auto" }}
  >
    <path
      d="M16.2803 8.28033C16.5732 7.98744 16.5732 7.51256 16.2803 7.21967C15.9874 6.92678 15.5126 6.92678 15.2197 7.21967L9.25 13.1893L6.78033 10.7197C6.48744 10.4268 6.01256 10.4268 5.71967 10.7197C5.42678 11.0126 5.42678 11.4874 5.71967 11.7803L8.71967 14.7803C9.01256 15.0732 9.48744 15.0732 9.78033 14.7803L16.2803 8.28033Z"
      fill="#00D870"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0ZM1.5 11C1.5 5.75329 5.75329 1.5 11 1.5C16.2467 1.5 20.5 5.75329 20.5 11C20.5 16.2467 16.2467 20.5 11 20.5C5.75329 20.5 1.5 16.2467 1.5 11Z"
      fill="#00D870"
    />
  </svg>
);

const failedStatusSvg = (
  <svg
    width="23"
    height="21"
    viewBox="0 0 23 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ margin: "auto" }}
  >
    <path
      d="M12.4701 15.5044C12.4701 16.0567 12.0224 16.5044 11.4701 16.5044C10.9178 16.5044 10.4701 16.0567 10.4701 15.5044C10.4701 14.9521 10.9178 14.5044 11.4701 14.5044C12.0224 14.5044 12.4701 14.9521 12.4701 15.5044Z"
      fill="#F8CF3E"
    />
    <path
      d="M12.2201 7.25439C12.2201 6.84018 11.8843 6.50439 11.4701 6.50439C11.0559 6.50439 10.7201 6.84018 10.7201 7.25439V11.7544C10.7201 12.1686 11.0559 12.5044 11.4701 12.5044C11.8843 12.5044 12.2201 12.1686 12.2201 11.7544V7.25439Z"
      fill="#F8CF3E"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.30596 1.24844C10.2686 -0.416146 12.6716 -0.416145 13.6343 1.24844L22.6007 16.7528C23.5645 18.4195 22.3618 20.5044 20.4365 20.5044H2.50374C0.578433 20.5044 -0.62428 18.4195 0.339575 16.7528L9.30596 1.24844ZM12.3358 1.99938C11.9507 1.33354 10.9895 1.33354 10.6045 1.99937L1.63807 17.5037C1.25253 18.1704 1.73361 19.0044 2.50374 19.0044H20.4365C21.2066 19.0044 21.6877 18.1704 21.3022 17.5037L12.3358 1.99938Z"
      fill="#F8CF3E"
    />
  </svg>
);

export default function Admin() {
  const [adminParams] = useSearchParams();
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const {
    dotsAdminChannelMutation,
    guildChannels,
    guildCategories,
    createCategoryMutation,
    dailyDigestSubscriptionStatus,
    updateSubscriptionStatusMutation,
    updateUserDiscordTokensMutation,
  } = useDotsAdmin();
  const guildId = localStorage.getItem("guild-id") as string;
  const queryClient = useQueryClient();

  let dotsAdminChannelExist = false;
  let activeOnboardingCategoryExist = false;
  if (guildChannels.data) {
    dotsAdminChannelExist = guildChannels.data.channels.some(
      (channel) => channel.name === "dots-admin"
    );
  }
  let validTokenError = false;

  useEffect(() => {
    if (adminParams.get("code")) {
      updateUserDiscordTokensMutation.mutate(
        {
          code: adminParams.get("code") as string,
          guildId,
        },
        {
          onSuccess: () => {
            guildChannels.refetch();
            guildCategories.refetch();
          },
        }
      );
    } else {
      guildChannels.refetch();
      guildCategories.refetch();
    }
    dailyDigestSubscriptionStatus.refetch();
  }, []);

  if (guildCategories.data) {
    activeOnboardingCategoryExist = guildCategories.data.categories.some(
      (category) =>
        category.type === "onboarding" && category.status === "active"
    );
  }

  if (
    [
      (guildCategories.error as any)?.message,
      (guildChannels.error as any)?.message,
    ].includes("User has no valid discord tokens")
  ) {
    validTokenError = true;
  }

  const defaultValues = {
    name: "",
    type: "onboarding",
  };

  const categoryForm = useForm({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { handleSubmit, register, control, reset, formState } = categoryForm;

  const createCategoryOnSubmit = (data: any) => {
    const cleanData = {
      name: data.name,
      type: data.type === "other" ? data.other : data.type,
    };
    createCategoryMutation.mutate(cleanData, {
      onSuccess: () => {
        //Optimistic update
        queryClient.setQueryData(
          ["categories", guildId],
          (oldCategories: any) => {
            oldCategories.categories.unshift({
              name: cleanData.name,
              type: cleanData.type,
              status: "active",
            });
            return oldCategories;
          }
        );
        guildCategories.refetch();
        reset(defaultValues);
        setOpenCategoryModal(false);
        message.success("Discord Category created!");
        window.analytics.track("A discord category has been created");
      },
      onError: (err: any) => {
        const errorMsg = err.msg ? err.msg : err;
        message.error(errorMsg, 5);
        window.analytics.track("Error creating a discord category", {
          errorInfo: JSON.stringify(err),
        });
      },
    });
  };

  const cleanCategoryModalForm = () => {
    reset(defaultValues);
    setOpenCategoryModal(false);
  };

  const categoryModal = (
    <Modal
      visible={openCategoryModal}
      okButtonProps={{ hidden: true }}
      onCancel={cleanCategoryModalForm}
      footer={null}
      cancelButtonProps={{ hidden: true }}
      width={"40vw"}
      style={{ top: "8vh" }}
    >
      <form onSubmit={handleSubmit(createCategoryOnSubmit)}>
        <div className="create-discord-category-modal">
          <div className="category-title">Create a new category</div>
          <div className="category-items">
            <FormLabel label="Name" />
            <Input
              style={{ width: "100%" }}
              maxLength={100}
              placeholder={"my discord category"}
              {...register("name", { required: true })}
            />
            {formState.errors.name && (
              <span role="alert" style={{ color: "red" }}>
                This field is required
              </span>
            )}
            <FormLabel label="Type" />
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <>
                  <Select
                    showSearch
                    placeholder={"Select category type"}
                    onChange={field.onChange}
                    style={{ width: "100%" }}
                    value={field.value}
                  >
                    {DotsCategories.types.map((categoryType, index) => {
                      return (
                        <Select.Option
                          key={categoryType.value}
                          value={categoryType.value}
                          name={categoryType.label}
                          id={index}
                        >
                          {categoryType.label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </>
              )}
            />
            <Button
              type="primary"
              htmlType={"submit"}
              style={{ width: "100%" }}
              loading={createCategoryMutation.isLoading}
              disabled={createCategoryMutation.isLoading}
            >
              Create
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );

  const authorizeButton = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "18px",
      }}
    >
      <a href={createDiscordUrl("discord-tokens")}>
        <Button
          size="large"
          type="default"
          style={{
            lineHeight: "normal",
            fontWeight: "bold",
            borderWidth: "2px",
            paddingLeft: "30px",
            paddingRight: "30px",
            height: "55px",
          }}
          icon={
            <Icon
              component={() => (
                <img src={"/discord.svg"} alt="discord" width="18" />
              )}
            />
          }
        >
          Authorize dots
        </Button>
      </a>
    </div>
  );

  const userErrorUserComponent = (
    <div>
      Oh, sorry! We don't have your channels and categories that the Dots bot
      needs to work properly in your server. To give us permissions, please just
      hit the button below.
      {authorizeButton}
    </div>
  );

  const loadedComponent = (
    <>
      {categoryModal}

      <Row>
        <p>
          <Switch
            checked={
              dailyDigestSubscriptionStatus.isSuccess
                ? dailyDigestSubscriptionStatus.data[0].send_daily_digest
                : false
            }
            loading={
              !dailyDigestSubscriptionStatus.isSuccess ||
              dailyDigestSubscriptionStatus.isLoading ||
              updateSubscriptionStatusMutation.isLoading
            }
            onChange={(checked: boolean) => {
              updateSubscriptionStatusMutation.mutate(checked, {
                onSuccess: () => {
                  message.success(
                    `Subscription status ${
                      checked ? "activated" : "deactivated"
                    }`
                  );
                  window.analytics.track(
                    `Dots admin - Subscription status ${
                      checked ? "activated" : "deactivated"
                    }`
                  );
                  dailyDigestSubscriptionStatus.refetch();
                },
                onError: (err: any) => {
                  const errorMsg = err.msg ? err.msg : err;
                  message.error(errorMsg, 5);
                  window.analytics.track(
                    "Error updating daily digest subscription status",
                    {
                      errorInfo: JSON.stringify(errorMsg),
                    }
                  );
                },
              });
            }}
          />{" "}
          Daily digest on discord?
        </p>
      </Row>

      {!validTokenError ? (
        <>
          <Row>
            <Col span={6}></Col>
            <Col span={4}>
              <h4 className={"text-center"}> Status</h4>
            </Col>
            <Col span={14}></Col>
          </Row>
          <Row>
            <Col span={6}>
              <div className="flex justify-between">
                <h4>Dots admin channel</h4>
                <Popover
                  content={
                    <p className="m-0 w-96">
                      This is a channel Dots uses to test automations and send
                      activity digests, as well as other important updates. If
                      you delete this, then Dots can't help you effectively test
                      automations.
                    </p>
                  }
                  placement="bottomLeft"
                >
                  <div className="pr-2">
                    <InfoCircleOutlined />
                  </div>
                </Popover>
              </div>
            </Col>
            <Col span={4}>
              <h4 className={"text-center"}>
                {guildChannels.isLoading || guildChannels.isFetching ? (
                  <Spin></Spin>
                ) : dotsAdminChannelExist ? (
                  successStatusSvg
                ) : (
                  failedStatusSvg
                )}
              </h4>
            </Col>
            <Col span={14}>
              <Button
                className={"setting-admin-button"}
                disabled={
                  dotsAdminChannelMutation.isLoading ||
                  guildChannels.isLoading ||
                  guildChannels.isFetching ||
                  dotsAdminChannelExist
                }
                loading={dotsAdminChannelMutation.isLoading}
                onClick={() =>
                  dotsAdminChannelMutation.mutate(undefined, {
                    onSuccess: () => {
                      message.success("Dots-admin channel created!");
                      window.analytics.track("Dots-admin channel created");
                      guildChannels.refetch();
                    },
                    onError: (err: any) => {
                      const errorMsg = err.msg ? err.msg : err;
                      message.error(errorMsg, 5);
                      window.analytics.track(
                        "Error creating dots-admin channel",
                        {
                          errorInfo: JSON.stringify(errorMsg),
                        }
                      );
                    },
                  })
                }
              >
                Create a new channel
              </Button>
            </Col>
          </Row>

          <Row>
            <Col span={6}>
              <div className="flex justify-between">
                <h4>Dots admin categories </h4>
                <Popover
                  content={
                    <p className="m-0 w-96">
                      This is required to create private channels. If these
                      categories are deleted, then Dots cannot create new
                      channels. Please ensure there is at least one category
                      that is active.
                    </p>
                  }
                  placement="bottomLeft"
                >
                  <div className="pr-2">
                    <InfoCircleOutlined />
                  </div>
                </Popover>
              </div>
            </Col>
            <Col span={4}>
              <h4 className={"text-center"}>
                {guildCategories.isLoading || guildCategories.isFetching ? (
                  <Spin></Spin>
                ) : activeOnboardingCategoryExist ? (
                  successStatusSvg
                ) : (
                  failedStatusSvg
                )}
              </h4>
            </Col>
            <Col span={14}>
              <Button
                disabled={
                  createCategoryMutation.isLoading ||
                  guildCategories.isFetching ||
                  guildCategories.isLoading
                }
                className="setting-admin-button"
                onClick={() => setOpenCategoryModal(true)}
              >
                Add a new category
              </Button>
            </Col>
          </Row>

          {guildCategories.isLoading || guildCategories.isFetching ? (
            ""
          ) : (
            <AdminCategories
              dotsAdminChannelExist={dotsAdminChannelExist}
              categories={guildCategories.data?.categories}
            />
          )}
        </>
      ) : (
        userErrorUserComponent
      )}
    </>
  );

  return (
    <>
      {updateUserDiscordTokensMutation.isLoading ? (
        <Spin />
      ) : updateUserDiscordTokensMutation.isError ? (
        (updateUserDiscordTokensMutation.error as any).msg.includes(
          "User does not have access to guild"
        ) ? (
          <div>
            Sorry, the discord user you authorized does not have access to the
            guild <b> {localStorage.getItem("guild-name")} </b>. Please
            authorize a discord user that has access to this guild.
            {authorizeButton}
          </div>
        ) : (
          userErrorUserComponent
        )
      ) : (
        loadedComponent
      )}
    </>
  );
}
